/**
 * @fileoverview Component Organism to add internal notes in the chat of the Inbox (Mercately).
 * It allows the user to write and send internal notes not visible to the customer, with validation, counter and immediate feedback.
 * Implements optimized architecture with separation of layers (Form/Actions), memoization and performance tracking.
 */
import React, {
  memo,
  useMemo,
  useRef,
  useCallback
} from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ButtonComponent from "../../../components/ButtonMC";
import IconMC from "../../../components/IconMC";
import useSelectedConversation from "../../../hooks/shared/useSelectedConversation";
import IsolatedChatNoteFormMC from "./IsolatedChatNoteFormMC";

const ChatNoteComponentMC = memo(({ handleToggleChatMode }) => {
  const { t } = useTranslation('InboxMC');
  const conversation = useSelectedConversation();
  const currentRetailerUser = useSelector((state) => state.mainReducer.currentRetailerUser);
  const textAreaRef = useRef(null);
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  const currentRetailerUserRef = useRef(currentRetailerUser);
  currentRetailerUserRef.current = currentRetailerUser;

  const conversationId = conversation?.id ?? null;

  const getConversationMeta = useCallback(() => {
    const agent = currentRetailerUserRef.current || {};
    const agentName = `${agent.first_name || ''} ${agent.last_name || ''}`.trim() || agent.email || '';
    const hasAgentId = agent.id !== undefined && agent.id !== null && agent.id !== '';
    return {
      customer_id: conversationRef.current?.id || null,
      note: true,
      platform: conversationRef.current?.platform || null,
      type: 'text',
      content_type: 'text',
      isComment: conversationRef.current?.isComment || false,
      ...(hasAgentId && agentName ? { agent_id: Number(agent.id), agent_name: agentName } : {})
    };
  }, []);

  const translations = useMemo(() => ({
    createNote: t('chat_note.create_note'),
    closeNote: t('chat_note.close_note'),
    addNote: t('chat_note.add'),
    notVisible: t('messages.note_not_visible_to_customer')
  }), [t]);

  return (
    <div className="tw-wrapper tw-bg-yellow-50 tw-p-0 tw-w-full">
      <div className="tw-flex tw-bg-yellow-200 tw-pt-1 tw-pb-1 tw-pl-4 tw-items-center tw-justify-between tw-rounded-t">
        <div className="tw-text-base tw-font-semibold tw-text-yellow-950">{translations.createNote}</div>
        <ButtonComponent
          outlined
          variant="SECONDARY"
          size="xs"
          icon="close-outline"
          onClick={handleToggleChatMode}
          className="tw-border-0 tw-bg-yellow-200 !tw-pt-1"
          iconClassName="tw-text-gray-950 !tw-size-[24px]"
          title={translations.closeNote}
        />
      </div>

      <div className="tw-flex tw-items-center tw-px-4 tw-py-2">
        <div className="tw-text-yellow-600 tw-flex tw-items-center tw-justify-start tw-gap-0 tw-mb-1" role="alert">
          <IconMC name="alert-circle-outline" className="tw-h-4 tw-w-4" />
          <span className="tw-text-xs tw-font-medium tw-ml-1">{translations.notVisible}</span>
        </div>
      </div>

      <IsolatedChatNoteFormMC
        textAreaRef={textAreaRef}
        conversationId={conversationId}
        getConversationMeta={getConversationMeta}
        addNoteLabel={translations.addNote}
      />
    </div>
  );
});

ChatNoteComponentMC.displayName = "ChatNoteComponentMC";

export default ChatNoteComponentMC;
